import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import './status-screen.css';

/**
 * What Artwork shows when an address leads nowhere (404) and when it crashes: direction C,
 * "big type" (Mohamad's pick of five, 2 Nov 2026). The message is set large in the system's
 * display face, with the ways out under it, on Artwork's own canvas: the rulers along the top and
 * left, the words on an artboard wearing the working page's ink outline, and the title a selected
 * text layer with its handles and its size. On a crash the outline and the selection turn red and
 * the size says it couldn't draw. The canvas parts are drawn, not read out.
 *
 * No router hooks here: whoever renders it says what each button does.
 */
export type StatusAction = { label: string; onClick: () => void; primary?: boolean };

type Props = {
  kind?: 'not-found' | 'crash';
  /** The two lines of the title; each kind has its own. */
  title?: [string, string];
  /** The sentence under the title. */
  line?: string;
  /** Primary first. */
  actions?: StatusAction[];
  /** An error report's id: shown short, Copy takes the whole id. */
  reference?: string | null;
  /** What threw, shown on a developer's machine only. */
  error?: unknown;
  /** Fills the window; otherwise it fills the frame it is in. */
  page?: boolean;
};

const TITLES: Record<'not-found' | 'crash', [string, string]> = {
  'not-found': ['This page', 'isn’t here.'],
  crash: ['Something', 'went wrong.'],
};

export function StatusScreen({ kind = 'not-found', title, line, actions = [], reference, error, page = false }: Props) {
  const crash = kind === 'crash';
  const [first, second] = title ?? TITLES[kind];
  const headingId = useId();
  return (
    <section
      className="status-screen"
      data-kind={crash ? 'crash' : 'not-found'}
      data-page={page ? '' : undefined}
      role={crash ? 'alert' : undefined}
      aria-labelledby={headingId}
    >
      <div className="artwork-art-rulers" aria-hidden="true" />
      <div className="status-screen-layout">
        {/* A crash is not a missing page, so the board has no number to give. */}
        <span className="artwork-art-board-name" aria-hidden="true">
          {crash ? 'Page —' : 'Page 404'}
        </span>
        <div className="status-screen-body">
          <p className="status-screen-code">
            <span className="status-screen-dot" aria-hidden="true" />
            {crash ? 'Error' : '404'} · Artwork
          </p>
          <Selected crash={crash}>
            <h1 className="status-screen-title" id={headingId}>
              <span>{first}</span> <span>{second}</span>
            </h1>
          </Selected>
          {line ? <p className="status-screen-line">{line}</p> : null}
          {reference ? <Reference id={reference} /> : null}
          {import.meta.env.DEV && error ? <pre className="status-screen-detail">{String((error as Error)?.stack || error)}</pre> : null}
          {actions.length ? (
            <div className="status-screen-actions">
              {actions.map((action) => (
                <button
                  key={action.label}
                  type="button"
                  className={action.primary ? 'status-screen-button is-primary' : 'status-screen-button'}
                  onClick={action.onClick}
                >
                  {action.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

/**
 * The title as a selected text layer: its outline, eight handles and, under it, its size as the
 * editor's badge gives it, measured from the title as drawn (W — H — until it is).
 */
function Selected({ crash, children }: { crash: boolean; children: ReactNode }) {
  const box = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const measure = () => {
      const { width, height } = el.getBoundingClientRect();
      setSize(width && height ? { w: Math.round(width), h: Math.round(height) } : null);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const watch = new ResizeObserver(measure);
    watch.observe(el);
    return () => watch.disconnect();
  }, []);
  return (
    <div ref={box} className="artwork-art-selection">
      {children}
      <span className="artwork-art-handles" aria-hidden="true">
        {HANDLES.map((at) => (
          <i key={at} data-at={at} />
        ))}
        <b className="artwork-art-size">{crash ? 'Couldn’t draw' : size ? `W ${size.w}  H ${size.h}` : 'W —  H —'}</b>
      </span>
    </div>
  );
}

const HANDLES = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'] as const;

/** An error report's id, so a report can be matched to the crash. */
function Reference({ id }: { id: string }) {
  const [copied, setCopied] = useState(false);
  const code = useRef<HTMLElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1600);
    } catch {
      // The browser refused the clipboard: select the code so it can be copied by hand.
      const selection = window.getSelection?.();
      if (selection && code.current) selection.selectAllChildren(code.current);
    }
  };
  return (
    <p className="status-screen-ref">
      <span>Reference</span>
      <code ref={code} title={id}>
        {id.slice(0, 8)}
      </code>
      <button type="button" onClick={copy}>
        {copied ? 'Copied' : 'Copy'}
      </button>
    </p>
  );
}
